<template>
    <algolia-places-get
        :per-page="3"
    >
        <div
            slot-scope="{
                data: places,
                search
            }"
        >
            <div class="container mx-auto max-w-2xl pt-8 xs:pt-10 pb-16 md:pt-18 md:pb-24">
                <div class="max-w-md mx-auto text-center mb-6 md:mb-12">
                    <h1 class="font-noto-sans font-semibold text-4xl lg:text-6xl text-grey-darkest leading-tight tracking-tight mb-6">
                        Find your perfect home
                    </h1>
                </div>

                <div class="relative max-w-md mx-auto">
                    <base-form
                        @submit="() => {
                            if (!places.length) return

                            $router.push({
                                name: 'listings.in.index',
                                params: {
                                    location: locationByPlace(places[0])
                                }
                            })
                        }"
                    >
                        <div class="flex items-center w-full px-2 py-1 xs:px-4 xs:py-2 bg-white rounded shadow-md md:px-6 md:py-3 focus-within:shadow-outline">
                            <base-svg
                                name="icon-location-pin"
                                class="w-6 h-6 flex-none"
                            />

                            <input
                                type="text"
                                class="input border-none focus:shadow-none"
                                placeholder="Address, Town, City or Postcode"
                                @input="event => {
                                    form.location = event.target.value
                                    search(form.location)
                                }"
                            >
                        </div>

                        <div
                            v-if="places.length"
                            class="absolute inset-x-0 bg-grey-bg border-t rounded-b shadow p-4 md:p-6 mx-2 md:mx-3"
                        >
                            <router-link
                                v-for="(place, index) in places"
                                :key="index"
                                :to="{
                                    name: 'listings.in.index',
                                    params: {
                                        location: locationByPlace(place)
                                    }
                                }"
                                class="inline-flex w-full items-center text-blue mb-4 last-child:mb-0"
                            >
                                <truncate-hack class="flex-grow flex items-center">
                                    {{ locationByPlace(place) }}
                                </truncate-hack>

                                <span
                                    v-if="index == 0"
                                    class="mx-2 text-sm text-grey"
                                >
                                    (Enter)
                                </span>

                                <base-svg
                                    name="icon-cheveron-right"
                                    class="fill-primary-blue h-6 w-6"
                                />
                            </router-link>
                        </div>
                    </base-form>

                    <router-link
                        :to="{ name: 'www.landlords.index' }"
                        class="flex items-center text-blue mt-4 ml-3"
                    >
                        Are you a landlord or agent?

                        <base-svg
                            name="icon-cheveron-right"
                            class="w-6 h-6 flex-none fill-primary-blue"
                        />
                    </router-link>
                </div>
            </div>

            <svg
                class="w-full h-10 text-white"
                preserveAspectRatio="none"
                viewBox="0 0 375 40"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path d="M375 40V0s-87.046 32-187.5 32S0 0 0 0v40h375z" />
            </svg>

            <div class="bg-white -mt-3 py-16 md:py-24 lg:py-32">
                <div class="container max-w-3xl mx-auto">
                    <div class="mx-auto text-center lg:w-1/2 pb-16 lg:pb-24">
                        <h2 class="mb-4 font-noto-sans text-2xl font-semibold leading-tight tracking-tight xs:text-3xl md:text-4xl lg:text-5xl text-grey-darkest">
                            We're making renting<br>
                            <span class="text-blue">better for everyone</span>
                        </h2>

                        <p class="text-lg lg:text-xl">
                            Rentora makes it easy to list properties, find tenants, manage tenancies, collect rent and more.
                        </p>
                    </div>

                    <grid-layout>
                        <grid-layout-item class="mb-12 md:mb-0 md:w-1/2">
                            <h6 class="text-lg text-grey-darkest font-noto-sans font-bold mb-6">
                                Landlords &amp; Agents
                            </h6>

                            <ul class="mb-6">
                                <li
                                    v-for="(perk, index) in [
                                        'Market properties on Rentora',
                                        'Collect and track rent payments',
                                        'Request one-time payments for move-in costs, utilities, etc.',
                                        'Share property listings on social media',
                                        'Accept online rental applications',
                                        'Manage an unlimited number of properties',
                                        'Send/receive instant messages',
                                        'Receive notifications via email and sms',
                                        'Upload and share inventories',
                                        'Upload and share important documents',
                                        'Upcoming event reminders (e.gj expiring documents)',
                                        'Collect payments from an unlimited number of tenants/roommates',
                                    ]"
                                    :key="index"
                                    class="flex items-top mb-3"
                                >
                                    <base-svg
                                        name="icon-check-circle"
                                        class="flex-none w-6 h-6 fill-primary-white fill-secondary-blue mr-3"
                                    />

                                    {{ perk }}
                                </li>
                            </ul>

                            <router-link
                                :to="{
                                    name: 'www.landlords.index'
                                }"
                                class="flex items-center text-blue"
                            >
                                Learn more

                                <base-svg
                                    name="icon-cheveron-right"
                                    class="w-6 h-6 fill-primary-blue"
                                />
                            </router-link>
                        </grid-layout-item>

                        <grid-layout-item class="md:w-1/2">
                            <h6 class="text-lg text-grey-darkest font-noto-sans font-bold mb-6">
                                Tenants
                            </h6>

                            <ul class="mb-6">
                                <li
                                    v-for="(perk, index) in [
                                        'Pay rent online',
                                        'Set up recurring Smart payments',
                                        'Flexible rent payments',
                                        'Send/receive instant messages',
                                        'View important documents',
                                        'View inventories',
                                        'Split the bill with roommates',
                                        'Receive notifications via email and sms',
                                        'Receive reminders for upcoming events (i.e. rent, expiring documents)',
                                        'Build your credit history just by paying rent',
                                        'Apply to properties listed on Rentora',
                                    ]"
                                    :key="index"
                                    class="flex items-top mb-3"
                                >
                                    <base-svg
                                        name="icon-check-circle"
                                        class="flex-none w-6 h-6 fill-primary-white fill-secondary-blue mr-3"
                                    />

                                    {{ perk }}
                                </li>
                            </ul>

                            <router-link
                                :to="{
                                    name: 'www.tenants.index'
                                }"
                                class="flex items-center text-blue"
                            >
                                Learn more

                                <base-svg
                                    name="icon-cheveron-right"
                                    class="w-6 h-6 fill-primary-blue"
                                />
                            </router-link>
                        </grid-layout-item>
                    </grid-layout>
                </div>
            </div>

            <div class="font-semibold text-blue-lightest bg-blue-bg-darker">
                <div class="container max-w-3xl mx-auto">
                    <div class="max-w-md py-12 mx-auto md:pt-24">
                        <h2 class="mb-12 font-noto-sans text-3xl font-semibold text-center text-white xs:text-4xl lg:text-5xl">
                            Popular cities
                        </h2>

                        <grid-layout class="md:text-xl">
                            <grid-layout-item
                                v-for="(city, index) in ['London', 'Sheffield', 'Nottingham', 'Manchester', 'Birmingham', 'Liverpool', 'Glasgow', 'Edinburgh', 'Newcastle', 'Leeds', 'Cardiff', 'Doncaster']"
                                :key="index"
                                class="w-1/2 md:w-1/3 mb-4"
                            >
                                <router-link
                                    :to="{
                                        name: 'listings.in.index',
                                        params: {
                                            location: city.toLowerCase()
                                        }
                                    }"
                                >
                                    {{ city }}
                                </router-link>
                            </grid-layout-item>
                        </grid-layout>
                    </div>

                    <grid-layout class="md:text-lg">
                        <grid-layout-item class="lg:w-8/12">
                            <router-link
                                :to="{ name: 'listings.index' }"
                                class="inline-flex items-center text-lg font-bold text-white mb-3"
                            >
                                Major UK Cities

                                <base-svg
                                    name="icon-cheveron-right"
                                    class="fill-primary-white h-5 w-5"
                                />
                            </router-link>

                            <grid-layout class="mb-6">
                                <grid-layout-item
                                    v-for="(region, key) in regions['uk'].children"
                                    :key="key"
                                    class="w-1/3 md:w-1/4 mt-1"
                                >
                                    <router-link
                                        :to="{
                                            name: 'listings.regions.index',
                                            params: {
                                                category: 'uk',
                                                subcategory: key
                                            }
                                        }"
                                    >
                                        {{ region.name }}
                                    </router-link>
                                </grid-layout-item>
                            </grid-layout>
                        </grid-layout-item>

                        <grid-layout-item class="lg:w-4/12">
                            <router-link
                                :to="{ name: 'listings.index' }"
                                class="inline-flex items-center text-lg font-bold text-white mb-3"
                            >
                                Popular London regions

                                <base-svg
                                    name="icon-cheveron-right"
                                    class="fill-primary-white h-5 w-5"
                                />
                            </router-link>

                            <grid-layout class="mb-6">
                                <grid-layout-item
                                    v-for="(region, key) in regions['london'].children"
                                    :key="key"
                                    class="w-1/3 md:w-1/4 mt-1"
                                >
                                    <router-link
                                        :to="{
                                            name: 'listings.regions.index',
                                            params: {
                                                category: 'london',
                                                subcategory: key
                                            }
                                        }"
                                    >
                                        {{ region.name }}
                                    </router-link>
                                </grid-layout-item>
                            </grid-layout>
                        </grid-layout-item>
                    </grid-layout>
                </div>
            </div>
        </div>
    </algolia-places-get>
</template>

<script>
    import _ from 'lodash'
    import HeadMixin from '../../mixins/head'

    export default {
        mixins: [
            HeadMixin
        ],
        data () {
            return {
                form: {
                    location: ''
                }
            }
        },
        computed: {
            regions () {
                return _.preload('regions')
            }
        },
        methods: {
            head () {
                return {
                    title: [
                        'Properties to rent',
                        'UK houses and flats to rent'
                    ]
                }
            },
            onSubmit () {
                this.$route.push({
                    name: 'listings.in.index',
                    params: {
                        location: this.form.location
                    }
                })
            },
            locationByPlace (place) {
                if (!place) {
                    return null
                }

                const loc = []

                if (place.locale_names) {
                    loc.push(_.first(place.locale_names))
                }

                if (place.city) {
                    loc.push(_.first(place.city))
                }

                if (place.county) {
                    loc.push(_.first(place.county))
                } else if (place.administrative) {
                    loc.push(_.last(place.administrative))
                }

                return loc.join(', ')
            }
        }
    }
</script>
